import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { academyAPI, consentAPI } from '../services/api';
import { TRACKS, getTrackForRole, AcademyTrack, AcademyModuleData } from '../data/academyContent';
import './Academy.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ModuleRecord {
  moduleIndex: number;
  score: number;
  passed: boolean;
  completedAt: string;
}

interface ProgressResponse {
  isSuperAdmin: boolean;
  trackName: string | null;
  role: string;
  allPassed: boolean;
  modules: ModuleRecord[];
}

type ModulePhase = 'reading' | 'testing' | 'result';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Renders text with \n\n paragraph breaks and **bold** support */
function renderContent(text: string): React.ReactNode[] {
  return text.split('\n\n').map((para, pIdx) => {
    const parts = para.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={pIdx} className="academy-reading-para">
        {parts.map((part, i) =>
          part.startsWith('**') && part.endsWith('**')
            ? <strong key={i}>{part.slice(2, -2)}</strong>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </p>
    );
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ModuleCardProps {
  module: AcademyModuleData;
  status: 'locked' | 'available' | 'passed';
  record?: ModuleRecord;
  onStart: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, status, record, onStart }) => (
  <div className={`academy-module-card academy-module-card--${status}`}>
    <div className="academy-module-card-header">
      <span className="academy-module-number">Module {module.index + 1}</span>
      <span className={`academy-badge academy-badge--${status}`}>
        {status === 'passed' ? '✓ Passed' : status === 'available' ? 'In Progress' : 'Locked'}
      </span>
    </div>
    <h3 className="academy-module-title">{module.title}</h3>
    <p className="academy-module-question-count">5 questions · pass mark 4/5</p>
    {status === 'passed' && record && (
      <div className="academy-module-meta">
        <span className="academy-module-meta-score">Score: {record.score}/5</span>
        <span>Completed: {new Date(record.completedAt).toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric',
        })}</span>
      </div>
    )}
    {status !== 'locked' && (
      <button className="academy-module-btn" onClick={onStart}>
        {status === 'passed' ? 'Review' : 'Start'}
      </button>
    )}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const Academy: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const track: AcademyTrack | null = user ? getTrackForRole(user.role) : null;

  // ── Progress state ──────────────────────────────────────────────────────────
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [moduleRecords, setModuleRecords]   = useState<ModuleRecord[]>([]);
  const [allPassed, setAllPassed]           = useState(false);
  const [downloadingManual, setDownloadingManual] = useState(false);

  // ── Declaration state ──
  // Initialise from localStorage so it persists across refreshes
  const [declarationSigned, setDeclarationSigned] = useState(
    () => !!(user?.id && localStorage.getItem(`academyDeclarationSigned-${user.id}`))
  );
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [declarationSubmitting, setDeclarationSubmitting] = useState(false);
  const [declarationError, setDeclarationError] = useState<string | null>(null);

  // Re-check localStorage once user loads (handles cold-start where user was null at init)
  useEffect(() => {
    if (user?.id && localStorage.getItem(`academyDeclarationSigned-${user.id}`)) {
      setDeclarationSigned(true);
    }
  }, [user?.id]);

  // ── Module interaction state ────────────────────────────────────────────────
  const [activeModuleIdx, setActiveModuleIdx] = useState<number | null>(null);
  const [phase, setPhase]                     = useState<ModulePhase>('reading');
  const [answers, setAnswers]                 = useState<(number | null)[]>(Array(5).fill(null));
  const [submitting, setSubmitting]           = useState(false);
  const [lastScore, setLastScore]             = useState<number | null>(null);
  const [downloadingCert, setDownloadingCert] = useState(false);
  // Toggle between short summary and full in-depth manual
  const [readingView, setReadingView] = useState<'short' | 'full'>('short');

  // ── SuperAdmin browse state ─────────────────────────────────────────────────
  const [expandedBrowseTrack, setExpandedBrowseTrack]   = useState<number | null>(null);
  const [expandedBrowseModule, setExpandedBrowseModule] = useState<{ track: number; module: number } | null>(null);

  // Derived: which module indices have been passed
  const passedModules = moduleRecords.filter(r => r.passed).map(r => r.moduleIndex);

  // ── Load progress ───────────────────────────────────────────────────────────
  const loadProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await academyAPI.getProgress();
      const data = res.data as ProgressResponse | null;
      setModuleRecords(data?.modules ?? []);
      setAllPassed(data?.allPassed ?? false);
    } catch {
      setError('Failed to load your Academy progress. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Download training manual ────────────────────────────────────────────────
  const handleDownloadManual = async () => {
    if (!track) return;
    setDownloadingManual(true);
    try {
      const modules = track.modules.map(m => ({ title: m.title, content: m.manualContent }));
      const res = await academyAPI.downloadTrainingManual(
        track.role, track.trackName, modules
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Myshield-${track.trackName.replace(/\s+/g, '-')}-Training-Manual.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Manual download failed. Please try again.');
    } finally {
      setDownloadingManual(false);
    }
  };

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // ── Module helpers ──────────────────────────────────────────────────────────
  const isUnlocked = (idx: number): boolean =>
    idx === 0 || passedModules.includes(idx - 1);

  const getStatus = (idx: number): 'locked' | 'available' | 'passed' => {
    if (passedModules.includes(idx)) return 'passed';
    if (isUnlocked(idx)) return 'available';
    return 'locked';
  };

  // ── Open a module ───────────────────────────────────────────────────────────
  const openModule = (idx: number) => {
    setActiveModuleIdx(idx);
    setPhase('reading');
    setReadingView('short');
    setAnswers(Array(5).fill(null));
    setLastScore(null);
  };

  const closeModule = () => {
    setActiveModuleIdx(null);
    setPhase('reading');
    setReadingView('short');
    setAnswers(Array(5).fill(null));
    setLastScore(null);
  };

  // ── Submit test ─────────────────────────────────────────────────────────────
  const handleSubmitTest = async () => {
    if (!track || activeModuleIdx === null) return;
    const module = track.modules[activeModuleIdx];
    let score = 0;
    answers.forEach((ans, i) => {
      if (ans === module.questions[i].correct) score++;
    });
    setLastScore(score);
    setSubmitting(true);
    const passed = score >= 4;
    const now = new Date().toISOString();
    // Optimistically update local record
    setModuleRecords(prev => {
      const existing = prev.findIndex(r => r.moduleIndex === activeModuleIdx);
      const newRecord: ModuleRecord = { moduleIndex: activeModuleIdx, score, passed, completedAt: now };
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = newRecord;
        return next;
      }
      return [...prev, newRecord];
    });
    try {
      const res = await academyAPI.submitModule(activeModuleIdx, score);
      const nowAllPassed = res.data?.allPassed;
      if (nowAllPassed) {
        setAllPassed(true);
        if (user?.id) localStorage.setItem(`academyDone-${user.id}`, 'true');
      }
    } catch {
      // Local update already applied — check if all passed locally
      const updatedPassed = moduleRecords
        .filter(r => r.moduleIndex !== activeModuleIdx)
        .filter(r => r.passed)
        .concat(passed ? [{ moduleIndex: activeModuleIdx, score, passed, completedAt: now }] : [])
        .filter(r => r.passed).length;
      if (updatedPassed >= 5) {
        setAllPassed(true);
        if (user?.id) localStorage.setItem(`academyDone-${user.id}`, 'true');
      }
    } finally {
      setSubmitting(false);
      setPhase('result');
    }
  };

  // ── Download certificate ────────────────────────────────────────────────────
  const handleDownloadCertificate = async () => {
    setDownloadingCert(true);
    try {
      const res = await academyAPI.downloadCertificate();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Myshield-Academy-Certificate.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Certificate download failed. Please try again.');
    } finally {
      setDownloadingCert(false);
    }
  };

  // ── Loading / error ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="academy-page">
        <div className="academy-loading">Loading Academy…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="academy-page">
        <div className="academy-error-banner">{error}</div>
      </div>
    );
  }

  // ── Declaration submit ──────────────────────────────────────────────────────────────
  const handleSignDeclaration = async () => {
    if (!declarationChecked || !track) return;
    setDeclarationSubmitting(true);
    setDeclarationError(null);
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    try {
      await consentAPI.log({
        consentType: 'AcademyDeclaration',
        clientId: user?.clientId ?? null,
        driverName: fullName || user?.username || null,
        details: `${fullName} confirmed completion of the '${track.trackName}' Academy training programme and declared readiness to use the Myshield portal in accordance with the guidelines provided. Declaration signed: ${today}.`,
        policyVersion: 'v1.0-2026-05-19',
      });
      // Persist so declaration is not shown again on refresh
      if (user?.id) localStorage.setItem(`academyDeclarationSigned-${user.id}`, 'true');
      setDeclarationSigned(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      setDeclarationError(`Declaration could not be saved${msg ? ': ' + msg : ''}. Please try again.`);
    } finally {
      setDeclarationSubmitting(false);
    }
  };

  // ── Completion screen ──────────────────────────────────────────────────────────────
  if (allPassed && !isSuperAdmin) {
    return (
      <div className="academy-page">
        <div className="academy-completion">
          <div className="academy-completion-icon">🎓</div>
          <h1 className="academy-completion-title">Training Complete!</h1>
          <p className="academy-completion-subtitle">
            Congratulations! You have completed the <strong>{track?.trackName}</strong> training programme.
          </p>

          {!declarationSigned ? (
            /* ── Declaration step ── */
            <div style={{
              width: '100%', maxWidth: 560,
              background: '#f0f4ff', border: '1px solid #c7d2fe',
              borderRadius: 10, padding: '24px 28px',
              textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#001f3f' }}>
                📝 Academy Completion Declaration
              </h3>
              <p style={{ margin: 0, fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
                Before downloading your certificate, please confirm the following declaration:
              </p>
              <div style={{
                background: '#ffffff', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '14px 18px',
                fontSize: 13.5, color: '#1a202c', lineHeight: 1.75,
              }}>
                <strong>I, {user?.firstName} {user?.lastName}</strong>, confirm that I have completed the{' '}
                <strong>{track?.trackName}</strong> training programme on the Myshield portal. I confirm
                that I have read and understood all training material and am prepared to use the Myshield
                portal in accordance with the guidelines provided. I agree to use the system responsibly
                and in compliance with the data protection and insurance requirements set out in the training.
              </div>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                cursor: 'pointer', fontSize: 13.5, color: '#374151',
              }}>
                <input
                  type="checkbox"
                  checked={declarationChecked}
                  onChange={e => setDeclarationChecked(e.target.checked)}
                  style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0, accentColor: '#001f3f' }}
                />
                I confirm the above declaration is true and I am happy to continue using the system as planned.
              </label>
              {declarationError && (
                <p style={{ margin: 0, fontSize: 13, color: '#c53030' }}>{declarationError}</p>
              )}
              <button
                onClick={handleSignDeclaration}
                disabled={!declarationChecked || declarationSubmitting}
                style={{
                  padding: '12px 28px', background: '#001f3f', color: '#fff',
                  border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 700,
                  cursor: declarationChecked && !declarationSubmitting ? 'pointer' : 'not-allowed',
                  opacity: declarationChecked && !declarationSubmitting ? 1 : 0.5,
                  alignSelf: 'center',
                }}
              >
                {declarationSubmitting ? 'Saving…' : '✓ Sign & Accept Declaration'}
              </button>
              <p style={{ margin: 0, fontSize: 11.5, color: '#718096', textAlign: 'center' }}>
                This declaration will be recorded in the Consent Records audit trail.
              </p>
            </div>
          ) : (
            /* ── Post-declaration: certificate available ── */
            <>
              <div style={{
                background: '#f0fff4', border: '1px solid #68d391',
                borderRadius: 8, padding: '12px 20px',
                fontSize: 13.5, color: '#276749', fontWeight: 600,
              }}>
                ✓ Declaration signed and recorded in Consent Records.
              </div>
              <p className="academy-completion-note">
                Your training completion and declaration have been logged.
                You may now download your certificate.
              </p>
              <button
                className="academy-cert-btn"
                onClick={handleDownloadCertificate}
                disabled={downloadingCert}
              >
                {downloadingCert ? 'Generating Certificate…' : '⬇ Download Certificate'}
              </button>
            </>
          )}

          <p className="academy-completion-review-note">
            You can always return to review the training materials and re-download your certificate.
          </p>
          <button className="academy-review-btn" onClick={() => setAllPassed(false)}>
            Review Training Materials
          </button>
        </div>
      </div>
    );
  }

  // ── SuperAdmin browse-only view ─────────────────────────────────────────────
  if (isSuperAdmin) {
    return (
      <div className="academy-page">
        <div className="academy-header">
          <h1 className="academy-title">Myshield Academy</h1>
          <p className="academy-subtitle">Training materials for all roles</p>
        </div>

        <div className="academy-browse-notice">
          <span className="academy-browse-notice-icon">ℹ</span>
          As Super Admin you are not required to complete the Academy. You may browse all training
          materials below.
        </div>

        <div className="academy-browse-tracks">
          {TRACKS.map((t, tIdx) => (
            <div key={tIdx} className="academy-browse-track">
              <button
                className={`academy-browse-track-header ${
                  expandedBrowseTrack === tIdx ? 'academy-browse-track-header--open' : ''
                }`}
                onClick={() =>
                  setExpandedBrowseTrack(expandedBrowseTrack === tIdx ? null : tIdx)
                }
              >
                <span className="academy-browse-track-name">{t.trackName}</span>
                <span className="academy-browse-track-role">({t.role})</span>
                <span className="academy-browse-chevron">
                  {expandedBrowseTrack === tIdx ? '▲' : '▼'}
                </span>
              </button>

              {expandedBrowseTrack === tIdx && (
                <div className="academy-browse-modules">
                  {t.modules.map((m, mIdx) => {
                    const isOpen =
                      expandedBrowseModule?.track === tIdx &&
                      expandedBrowseModule?.module === mIdx;
                    return (
                      <div key={mIdx} className="academy-browse-module">
                        <button
                          className={`academy-browse-module-header ${
                            isOpen ? 'academy-browse-module-header--open' : ''
                          }`}
                          onClick={() =>
                            setExpandedBrowseModule(
                              isOpen ? null : { track: tIdx, module: mIdx }
                            )
                          }
                        >
                          <span>Module {m.index + 1}: {m.title}</span>
                          <span className="academy-browse-chevron">{isOpen ? '▲' : '▼'}</span>
                        </button>
                        {isOpen && (
                          <div className="academy-browse-content">
                            {renderContent(m.readingContent)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── No track for role (fallback) ────────────────────────────────────────────
  if (!track) {
    return (
      <div className="academy-page">
        <div className="academy-error-banner">
          No training track is available for your role. Please contact your administrator.
        </div>
      </div>
    );
  }

  // ── Active module view (reading / testing / result) ─────────────────────────
  if (activeModuleIdx !== null) {
    const module = track.modules[activeModuleIdx];
    const allAnswered = answers.every(a => a !== null);

    return (
      <div className="academy-page">
        {/* Back nav */}
        <div className="academy-module-nav">
          <button className="academy-back-btn" onClick={closeModule}>
            ← Back to modules
          </button>
          <span className="academy-module-nav-title">
            Module {module.index + 1}: {module.title}
          </span>
        </div>

        {/* ── READING phase ── */}
        {phase === 'reading' && (
          <div className="academy-phase-card">
            <div className="academy-phase-label">Reading Material</div>
            <h2 className="academy-phase-title">{module.title}</h2>

            {/* Short / Full manual toggle */}
            <div className="academy-reading-toggle">
              <button
                className={`academy-toggle-btn ${readingView === 'short' ? 'academy-toggle-btn--active' : ''}`}
                onClick={() => setReadingView('short')}
              >
                Short Summary
              </button>
              <button
                className={`academy-toggle-btn ${readingView === 'full' ? 'academy-toggle-btn--active' : ''}`}
                onClick={() => setReadingView('full')}
              >
                Full Training Manual
              </button>
            </div>

            <div className="academy-reading-box">
              {renderContent(readingView === 'full' ? module.manualContent : module.readingContent)}
            </div>
            <button
              className="academy-primary-btn"
              onClick={() => {
                setAnswers(Array(5).fill(null));
                setPhase('testing');
              }}
            >
              Start Test →
            </button>
          </div>
        )}

        {/* ── TESTING phase ── */}
        {phase === 'testing' && (
          <div className="academy-phase-card">
            <div className="academy-phase-label">Module Test — {module.title}</div>
            <p className="academy-test-intro">
              Answer all 5 questions. You need at least 4 correct to pass.
            </p>
            <div className="academy-questions">
              {module.questions.map((q, qIdx) => (
                <div key={qIdx} className="academy-question-card">
                  <p className="academy-question-text">
                    <span className="academy-question-num">Q{qIdx + 1}.</span> {q.question}
                  </p>
                  <div className="academy-options">
                    {q.options.map((opt, oIdx) => (
                      <label
                        key={oIdx}
                        className={`academy-option ${
                          answers[qIdx] === oIdx ? 'academy-option--selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q${qIdx}`}
                          value={oIdx}
                          checked={answers[qIdx] === oIdx}
                          onChange={() => {
                            const next = [...answers];
                            next[qIdx] = oIdx;
                            setAnswers(next);
                          }}
                          className="academy-radio"
                        />
                        <span className="academy-option-letter">
                          {['A', 'B', 'C', 'D'][oIdx]}
                        </span>
                        <span className="academy-option-text">{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="academy-test-footer">
              {!allAnswered && (
                <p className="academy-test-hint">Please answer all questions before submitting.</p>
              )}
              <button
                className="academy-primary-btn"
                onClick={handleSubmitTest}
                disabled={!allAnswered || submitting}
              >
                {submitting ? 'Submitting…' : 'Submit Answers'}
              </button>
            </div>
          </div>
        )}

        {/* ── RESULT phase ── */}
        {phase === 'result' && lastScore !== null && (
          <div className="academy-phase-card">
            <div
              className={`academy-result-banner ${
                lastScore >= 4 ? 'academy-result-banner--pass' : 'academy-result-banner--fail'
              }`}
            >
              <span className="academy-result-score">{lastScore} / 5</span>
              <span className="academy-result-verdict">
                {lastScore >= 4 ? '✓ Passed!' : '✗ Not quite — try again'}
              </span>
            </div>

            <div className="academy-question-results">
              {module.questions.map((q, qIdx) => {
                const chosen = answers[qIdx] ?? -1;
                const isCorrect = chosen === q.correct;
                return (
                  <div
                    key={qIdx}
                    className={`academy-qresult ${
                      isCorrect ? 'academy-qresult--correct' : 'academy-qresult--wrong'
                    }`}
                  >
                    <div className="academy-qresult-header">
                      <span className="academy-qresult-icon">{isCorrect ? '✓' : '✗'}</span>
                      <span className="academy-qresult-q">
                        Q{qIdx + 1}. {q.question}
                      </span>
                    </div>
                    {!isCorrect && (
                      <p className="academy-qresult-answer">
                        Your answer: <strong>{chosen >= 0 ? q.options[chosen] : '—'}</strong>
                        &nbsp;·&nbsp;
                        Correct answer: <strong>{q.options[q.correct]}</strong>
                      </p>
                    )}
                    <p className="academy-qresult-explanation">{q.explanation}</p>
                  </div>
                );
              })}
            </div>

            <div className="academy-result-footer">
              {lastScore >= 4 ? (
                <button className="academy-primary-btn" onClick={closeModule}>
                  Continue →
                </button>
              ) : (
                <>
                  <button
                    className="academy-secondary-btn"
                    onClick={() => {
                      setAnswers(Array(5).fill(null));
                      setPhase('testing');
                    }}
                  >
                    Try Again
                  </button>
                  <button className="academy-ghost-btn" onClick={() => setPhase('reading')}>
                    Re-read material
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Module overview ─────────────────────────────────────────────────────────
  const completedCount = passedModules.length;
  const progressPct = Math.round((completedCount / 5) * 100);

  return (
    <div className="academy-page">
      <div className="academy-header">
        <h1 className="academy-title">Myshield Academy</h1>
        <p className="academy-subtitle">{track.trackName} Training Programme</p>
      </div>

      {/* Progress bar */}
      <div className="academy-progress-section">
        <div className="academy-progress-header">
          <span className="academy-progress-label">
            {completedCount} / 5 Modules Completed
          </span>
          <span className="academy-progress-pct">{progressPct}%</span>
        </div>
        <div className="academy-progress-bar-track">
          <div
            className="academy-progress-bar-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="academy-progress-dots">
          {[0, 1, 2, 3, 4].map(i => (
            <span
              key={i}
              className={`academy-progress-dot ${
                passedModules.includes(i) ? 'academy-progress-dot--passed' : ''
              }`}
              title={`Module ${i + 1}: ${track.modules[i]?.title}`}
            >
              {passedModules.includes(i) ? '✓' : i + 1}
            </span>
          ))}
        </div>
      </div>

      {/* Module cards */}
      <div className="academy-modules">
        {track.modules.map((mod) => (
          <ModuleCard
            key={mod.index}
            module={mod}
            status={getStatus(mod.index)}
            record={moduleRecords.find(r => r.moduleIndex === mod.index)}
            onStart={() => openModule(mod.index)}
          />
        ))}
      </div>

      {/* If all passed but allPassed flag not yet set by API */}
      {passedModules.length >= 5 && !allPassed && (
        <div className="academy-all-complete-bar">
          🎓 You have passed all modules!&nbsp;
          <button
            className="academy-cert-btn-inline"
            onClick={handleDownloadCertificate}
            disabled={downloadingCert}
          >
            {downloadingCert ? 'Generating…' : '⬇ Download Certificate'}
          </button>
        </div>
      )}

      {/* ── Resources section ── */}
      <div className="academy-resources">
        <h3 className="academy-resources-title">📚 Training Resources</h3>
        <p className="academy-resources-subtitle">
          Download your full training manual for the <strong>{track.trackName}</strong> track.
          {allPassed && ' Your completion certificate is also available below.'}
        </p>
        <div className="academy-resources-btns">
          <button
            className="academy-resource-btn"
            onClick={handleDownloadManual}
            disabled={downloadingManual}
          >
            {downloadingManual ? 'Generating…' : '⬇ Download Training Manual (PDF)'}
          </button>
          {allPassed && (
            <button
              className="academy-resource-btn academy-resource-btn--outline"
              onClick={handleDownloadCertificate}
              disabled={downloadingCert}
            >
              {downloadingCert ? 'Generating…' : '🎓 Download Certificate'}
            </button>
          )}
        </div>
      </div>

      {/* ── Training log ── */}
      <div className="academy-log">
        <h3 className="academy-log-title">📋 My Training Log</h3>
        {moduleRecords.length === 0 ? (
          <p className="academy-log-empty">No modules completed yet. Start Module 1 to begin.</p>
        ) : (
          <table className="academy-log-table">
            <thead>
              <tr>
                <th>Module</th>
                <th>Score</th>
                <th>Result</th>
                <th>Date Completed</th>
              </tr>
            </thead>
            <tbody>
              {moduleRecords
                .slice()
                .sort((a, b) => a.moduleIndex - b.moduleIndex)
                .map(r => (
                  <tr key={r.moduleIndex}>
                    <td><strong>Module {r.moduleIndex + 1}:</strong> {track.modules[r.moduleIndex]?.title}</td>
                    <td>{r.score}/5</td>
                    <td>
                      <span className={r.passed ? 'academy-log-pass' : 'academy-log-fail'}>
                        {r.passed ? '✓ Pass' : '✗ Not Passed'}
                      </span>
                    </td>
                    <td>{new Date(r.completedAt).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}&nbsp;·&nbsp;{new Date(r.completedAt).toLocaleTimeString('en-GB', {
                      hour: '2-digit', minute: '2-digit',
                    })}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Academy;
